import { TSavedLaneSchema } from 'transfix-form/forms/SavedLaneForm/schema'
import { IDateTypes, IEquipmentTypes, IRateTypes } from 'transfix-form/forms/SavedLaneForm/types'
import { getIsoDays, parseLocation } from 'transfix-form/forms/SavedLaneForm/utils'
import { BookableShipmentSavedSearch, LaneAlert } from 'apps/marketplace/apiTypes/carrierLaneAlerts'

interface IGetBookableShipmentSavedSearchProps {
  savedLane: TSavedLaneSchema
  currentSubscribers?: LaneAlert['recipientSubscribers']
}

/**
 * Returns a BookableShipmentSavedSearch rateType value based on IRateTypes enum value
 */
const getRateType = (rateType: IRateTypes): BookableShipmentSavedSearch['rateType'] => {
  if (rateType === IRateTypes.ratePerMile || rateType === IRateTypes.totalAllIn) {
    return rateType
  }
  return null
}

/**
 * Generates a BookableShipmentSavedSearch object
 * from a TSavedLaneSchema object
 */
export const getBookableShipmentSavedSearch = ({
  currentSubscribers = [],
  savedLane,
}: IGetBookableShipmentSavedSearchProps): BookableShipmentSavedSearch => {
  const {
    date,
    dateType,
    daysOfWeek,
    equipment,
    lane,
    rate,
    rateType: savedLaneRateType,
    recipients,
  } = savedLane || {}

  const { dropoff, dropoffRadius, pickup, pickupRadius } = lane || {}
  const { text: pickupText } = pickup || {}
  const { text: dropoffText } = dropoff || {}
  const { city: pickupCity, stateCode: pickupStateCode } = parseLocation(pickupText) || {}
  const { city: deliveryCity = null, stateCode: deliveryStateCode = null } =
    parseLocation(dropoffText) || {}

  /**
   * Array of parent_user_ids from recipients in form
   */
  const newSubscriberParentIds = recipients.map(({ user }) => user.id)

  /**
   * Determine which subscribers are no longer in the saved lane and need to be destroyed
   */
  const subscribersToDestory = currentSubscribers.filter(
    ({ parentUserId }) => !newSubscriberParentIds.includes(`${parentUserId}`)
  )

  return {
    pickupCity,
    pickupStateCode,
    pickupMiles: pickupRadius,
    pickupStartDate: dateType === IDateTypes.specificDates ? date : null,
    pickupIsoDaysOfWeek:
      dateType === IDateTypes.daysOfWeek && daysOfWeek ? getIsoDays(daysOfWeek) : null,
    deliveryCity,
    deliveryStateCode,
    deliveryMiles: deliveryCity && deliveryStateCode ? dropoffRadius : null,
    trailerType: equipment === IEquipmentTypes.anyEquipment ? null : equipment,
    rate: rate || null,
    rateType: getRateType(savedLaneRateType),
    bookableShipmentSavedSearchSubscribersAttributes: [
      ...recipients.map(({ receiveNotifications, user }) => {
        const { id: userId } = user || {}
        return {
          parentUserId: Number(userId),
          hasNotificationsEnabled: receiveNotifications,
          destroy: false,
        }
      }),
      ...subscribersToDestory.map(({ hasNotificationsEnabled, parentUserId }) => ({
        parentUserId,
        hasNotificationsEnabled,
        destroy: true,
      })),
    ],
  }
}
